import React, { useEffect, useState, useRef } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import InterRegular from "./font/Inter_24pt-Regular-normal";
import InterBold from "./font/Inter_24pt-Bold-normal";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@heartinz/finance";
import { useShowAlert } from "../context/AlertContext";
import handleError from "../constants/handleError";
import uRLs from "../utils/baseUtils";
import { FormatedSaleData } from "../utils/GloabalSaleData";
import InvoiceLayout from "./common/InvoiceLayout ";
import InvoiceWrapper from "./common/InvoiceWrapper";

const StructuredInvoice = ({ isData }) => {
  const showAlert = useShowAlert(); 
  const [download, setDownload] = useState(false);
  const [print, setPrint] = useState(false);
  const [data, setData] = useState({});
  const globalData = FormatedSaleData(data);
  const selectedTheme = isData?.storeSettings?.invoiceSettings?.template;
  const terms = isData?.storeSettings?.invoiceSettings?.termsAndConditions;
  const isBillReceipt =
    globalData?.salemode?.toUpperCase() === "BILL" &&
    globalData?.storeSettings?.saleSettings?.saleModeSettings?.isTaxInfo;

  const documentTitle = isBillReceipt
    ? "BILL RECEIPT"
    : "TAX INVOICE";
 const invLabel = isBillReceipt ? "Bill #:" : "Invoice #:";

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;

  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  let createdTime = "--:--";
  if (data?.createdAt) {
    const zoned = DateConverter.convertToTimeZone(data.createdAt, timezone);
    createdTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  const loadImageDimensions = (base64Src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = base64Src;
    });
  };

  const urlToBase64 = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const joinAddress = (...lines) =>
    lines
      .map((l) => l?.trim())
      .filter(Boolean)
      .join(", ");

  const wrapText = (doc, text, width) => {
    if (!text) return [];
    return doc.splitTextToSize(text, width);
  };

  const generateInvoice1 = async () => {
    const doc = new jsPDF("p", "mm", "a4");

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const ensureSpace = (
      doc,
      currentY,
      requiredHeight,
      topMargin = 10,
      bottomMargin = 10
    ) => {
      const pageH = doc.internal.pageSize.getHeight();
      if (currentY + requiredHeight > pageH - bottomMargin) {
        doc.addPage();
        return topMargin;
      }
      return currentY;
    };

    const drawLabelValue = (
      doc,
      label,
      value,
      x,
      y,
      maxWidth,
      lh,
      labelBold = true // 👈 default bold
    ) => {
      if (!value) return y;

      const labelText = `${label}: `;

      doc.setFont(
        labelBold ? "Inter-Bold" : "Inter-Regular",
        labelBold ? "bold" : "normal"
      );

      const labelWidth = doc.getTextWidth(labelText);
      doc.text(labelText, x, y);

      doc.setFont("Inter-Regular", "normal");
      const wrapped = doc.splitTextToSize(value, maxWidth - labelWidth);
      doc.text(wrapped, x + labelWidth, y);

      return y + wrapped.length * lh;
    };

    /* -------------------------------------------------
     HEADER (AUTO HEIGHT)
  ------------------------------------------------- */
    const borderColor = "#C0C0C0";
    const headerY = 8;

    const leftBoxX = 8;
    const leftBoxW = 110; // ⬅️ reduced width
    const rightBoxX = leftBoxX + leftBoxW + 2;
    const rightBoxW = pageW - rightBoxX - 8;

    const titleHeight = 8;
    const lh = 3.8;

    const headerPaddingTop = 2;
    const headerPaddingBottom = 1;

    const contentX = leftBoxX + 32;
    const maxTextWidth = leftBoxW - 34;

    const address = joinAddress(
      globalData?.salefrom?.addressline1,
      globalData?.salefrom?.addressline2,
      globalData?.salefrom?.district,
      globalData?.salefrom?.state,
      globalData?.salefrom?.pincode
    );

    let tempHeight = headerPaddingTop + titleHeight;

    // company name font size effect
    if (address) {
      tempHeight += doc.splitTextToSize(address, maxTextWidth).length * lh;
    }

    [
      globalData?.salefrom?.phone,
      globalData?.salefrom?.email,
      globalData?.salefrom?.gstnumber,
      globalData?.salefrom?.fssai,
      globalData?.salefrom?.cin,
      globalData?.salefrom?.pannumber,
    ].forEach((v) => {
      if (v) tempHeight += lh;
    });

    tempHeight += headerPaddingBottom;
    const logoBlockHeight = globalData?.salefrom?.image
      ? headerPaddingTop + 18 + headerPaddingBottom
      : 0;

    // ---- Calculate RIGHT SIDE minimum height ---- //
    const metaRowHeight = 6; // each row height
    const metaTopSpacing = 5; // space from top before first row
    const metaRows = 5; // Invoice#, Date, POS, Due Date, Sales Person
    const rightMinHeight = metaTopSpacing + metaRows * metaRowHeight + 1;

    // ---- Final Header Height ---- //
    const headerH = Math.max(tempHeight, logoBlockHeight, rightMinHeight) - 1;

    // LEFT HEADER BOX
    const hexToRgb = (hex) => {
      if (!hex) return [0, 0, 0];
      const h = hex.replace("#", "");
      return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
      ];
    };

    const [r, g, b] = hexToRgb(borderColor);

    // Apply border color
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.3);
    doc.rect(leftBoxX, headerY, leftBoxW, headerH);

    // LOGO

    if (globalData?.salefrom?.image) {
      try {
        const imagePath =
          uRLs.IMAGE_URL + "profile/" + globalData.salefrom.image;

        // Convert to base64
        const base64 = await urlToBase64(imagePath);

        // Natural dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 🔥 Max logo area inside header box
        const maxWidth = 26;
        const maxHeight = headerH - 10;

        let imgW = width;
        let imgH = height;

        const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);
        imgW *= ratio;
        imgH *= ratio;

        // Detect format
        const ext = imagePath.split(".").pop().toLowerCase();
        const format =
          ext === "png"
            ? "PNG"
            : ext === "jpg" || ext === "jpeg"
            ? "JPEG"
            : "JPEG";

        // Draw image
        doc.addImage(base64, format, leftBoxX + 2, headerY + 5, imgW, imgH);
      } catch (err) {
        console.warn("Logo load failed:", err);
      }
    }

    // COMPANY NAME
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text(
      globalData?.salefrom?.title || "",
      contentX,
      headerY + headerPaddingTop + 4
    );

    // COMPANY DETAILS
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);

    let cty = headerY + headerPaddingTop + titleHeight;
    if (address) {
      doc.setFont("Inter-Regular", "normal");
      const addrLines = doc.splitTextToSize(address, maxTextWidth);
      doc.text(addrLines, contentX, cty);
      cty += addrLines.length * lh;
    }

    // CONTACT & TAX INFO
    cty = drawLabelValue(
      doc,
      "Phone",
      globalData?.salefrom?.phone,
      contentX,
      cty,
      maxTextWidth,
      lh,
      false // 👈 normal
    );

    cty = drawLabelValue(
      doc,
      "Email",
      globalData?.salefrom?.email,
      contentX,
      cty,
      maxTextWidth,
      lh,
      false // 👈 normal
    );

    cty = drawLabelValue(
      doc,
      "GST",
      globalData?.salefrom?.gstnumber,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    cty = drawLabelValue(
      doc,
      "FSSAI",
      globalData?.salefrom?.fssai,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    cty = drawLabelValue(
      doc,
      "CIN",
      globalData?.salefrom?.cin,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    cty = drawLabelValue(
      doc,
      "PAN",
      globalData?.salefrom?.pannumber,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    // RIGHT HEADER BOX
    doc.rect(rightBoxX, headerY, rightBoxW, headerH);
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);

    // Decide title text
    const invoiceText =
      globalData?.status === "CANCELLED"
        ? "CANCELLED"
        : globalData?.salemode
        ? documentTitle // eg: TAX INVOICE
        : "";

    // Optional: make CANCELLED stand out
    if (globalData?.status === "CANCELLED") {
      doc.setTextColor(200, 0, 0); // red
    }

    doc.text(invoiceText, rightBoxX + rightBoxW - 2, headerY + 3, {
      align: "right",
    });

    // reset color for rest of document
    doc.setTextColor(0, 0, 0);

    const invDate = DateConverter.ISOToDisplayFormat(globalData?.saledate).date;

    const meta = [
      ["Invoice #", globalData?.saleno],
      ["Date", invDate],
      ["Place Of Supply", globalData?.placeofsupply || "Tamil Nadu"],
      ["Due Date", invDate],
      ["Sales Person", globalData?.salesperson || ""],
    ];

    let my = headerY + 5;
    meta.forEach(([k, v]) => {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text(k, rightBoxX + 2, my);
      doc.text(":", rightBoxX + 30, my);

      doc.setFont("Inter-Regular", "normal");
      doc.text(String(v || "-"), rightBoxX + 34, my);
      my += 6;
    });

    /* -------------------------------------------------
     CUSTOMER & SHIPPING (AUTO HEIGHT)
  ------------------------------------------------- */
    const pagePadding = 8;
    const gapBetweenBoxes = 2;
    const innerPadding = 2;
    const titleGap = 4; // space below title

    // total available width
    const availableWidth = pageW - pagePadding * 2 - gapBetweenBoxes;
    const boxWidth = availableWidth / 2;

    // ✅ separate variables (no conflict with header)
    const custBoxX = pagePadding;
    const custBoxW = boxWidth;

    const shipBoxX = custBoxX + custBoxW + gapBetweenBoxes;
    const shipBoxW = boxWidth;

    const boxY = headerY + headerH + 2;
    let tableStartY;

    const hasSaleTo =
      globalData?.saleto &&
      (globalData.saleto.name ||
        globalData.saleto.addressline1 ||
        globalData.saleto.addressline2 ||
        globalData.saleto.district ||
        globalData.saleto.state ||
        globalData.saleto.pincode ||
        globalData.saleto.gstnumber);

    if (hasSaleTo ) {
      const lineHeight = 3;

      const innerTextWidthLeft = custBoxW - 4;
      const innerTextWidthRight = shipBoxW - 4;

      // ---------- CUSTOMER TEXT
      const customerText = [
        globalData?.saleto?.name,
        joinAddress(
          globalData?.saleto?.addressline1,
          globalData?.saleto?.addressline2
        ),
        `${globalData?.saleto?.district}, ${globalData?.saleto?.state} - ${globalData?.saleto?.pincode}`,
        globalData?.saleto?.phone ? `Phone : ${globalData.saleto.phone}` : "",
        globalData?.saleto?.email ? `Email : ${globalData.saleto.email}` : "",
        globalData?.saleto?.gstnumber
          ? `GSTIN : ${globalData.saleto.gstnumber}`
          : "",
        globalData?.saleto?.pannumber
          ? `PAN : ${globalData.saleto.pannumber}`
          : "",
        globalData?.saleto?.cinnumber
          ? `CIN : ${globalData.saleto.cinnumber}`
          : "",
      ].filter(Boolean);

      let customerLines = [];
      customerText.forEach((t) => {
        customerLines.push(...wrapText(doc, t, innerTextWidthLeft));
      });

      // ---------- SHIPPING TEXT
      const shippingText = [
        globalData?.saleto?.name,
        joinAddress(
          globalData?.saleto?.addressline1,
          globalData?.saleto?.addressline2
        ),
        `${globalData?.saleto?.district}, ${globalData?.saleto?.state}`,
        globalData?.transport || "",
      ].filter(Boolean);

      let shippingLines = [];
      shippingText.forEach((t) => {
        shippingLines.push(...wrapText(doc, t, innerTextWidthRight));
      });

      // ---------- HEIGHT CALCULATION ----//
      const customerTextStartY = boxY + innerPadding + 1 + titleGap;
      const shippingTextStartY = boxY + innerPadding + 1 + titleGap;
      const customerTextHeight = customerLines.length * lineHeight;
      const shippingTextHeight = shippingLines.length * lineHeight;
      const custBoxH = customerTextStartY - boxY + customerTextHeight + 1;

      const shipBoxH = shippingTextStartY - boxY + shippingTextHeight + 1;
      const boxH = Math.max(custBoxH, shipBoxH);

      // ---------- CUSTOMER BOX

      doc.rect(custBoxX, boxY, custBoxW, boxH);

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.text(
        "Customer Details",
        custBoxX + innerPadding,
        boxY + innerPadding + 3
      );

      doc.setFont("Inter-Regular", "normal");
      let cy = boxY + innerPadding + 3 + titleGap;

      customerLines.forEach((line) => {
        doc.text(line, custBoxX + innerPadding, cy);
        cy += lineHeight;
      });

      // ---------- SHIPPING BOX
      doc.rect(shipBoxX, boxY, shipBoxW, boxH);

      doc.setFont("Inter-Bold", "bold");
      doc.text(
        "Shipping Address",
        shipBoxX + innerPadding,
        boxY + innerPadding + 3
      );

      doc.setFont("Inter-Regular", "normal");
      let sy = boxY + innerPadding + 4 + titleGap;

      shippingLines.forEach((line) => {
        doc.text(line, shipBoxX + innerPadding, sy);
        sy += lineHeight;
      });

      tableStartY = boxY + boxH + 2;
    } else {
      tableStartY = boxY;
    }

    /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
    autoTable(doc, {
      startY: tableStartY,
      theme: "grid",
      margin: { left: 8, right: 5 },
      styles: {
        font: "Inter-Regular",
        fontSize: 7,
        cellPadding: 1,
        valign: "middle",
        overflow: "linebreak",
        lineWidth: 0.2,
        textColor: "#000000",
      },
      headStyles: {
        font: "Inter-Bold",
        fontSize: 7,
        halign: "center",
        valign: "middle",
        cellPadding: 0.8,
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
      },
      head: [
        [
          "S.No",
          "Description",
          "HSN/SAC",
          "MRP",
          "Base Price",
          "Qty",
          "Taxable Value",
          "Tax",
          "Tax Amount",
          "Amount",
        ],
      ],
      body: globalData?.saleitems.map((it, i) => {
        const rate = it.lineTotalTaxAmount;
        const amount = it.lineGrandTotal;
        const mrp = it.mrp;
        const baseprice = it.baseprice;
        const taxableValue = it.lineTotalBasePrice;

        return [
          i + 1,
          [it.title, it.note ? `-(${it.note})` : ""].filter(Boolean),
          it.hsnsac || "",
          `₹${mrp}`,
          `₹${baseprice}`,
          `${it.quantity} Pcs`,
          `₹${taxableValue}`,
          `${it.taxpercentage || 0}%`,
          `₹${rate}`,
          `₹${amount}`,
        ];
      }),
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 55, halign: "left" },
        2: { cellWidth: 16, halign: "center" },
        3: { cellWidth: 18, halign: "right" },
        4: { cellWidth: 18, halign: "right" },
        5: { cellWidth: 12, halign: "center" },
        6: { cellWidth: 18, halign: "right" },
        7: { cellWidth: 10, halign: "center" },
        8: { cellWidth: 18, halign: "right" },
        9: { cellWidth: 18, halign: "right" },
      },
    });

    /* -------------------------------------------------
     TOTALS
  ------------------------------------------------- */
    const isInterState =
      globalData?.salefrom?.statecode &&
      globalData?.saleto?.statecode &&
      globalData?.salefrom.statecode !== globalData?.saleto.statecode;

    const igst = globalData?.igstamount;
    const sgst = globalData?.sgstamount;
    const cgst = globalData?.cgstamount;
    const linetotalbaseprice = globalData?.totalbaseprice;
    const grantotal = globalData?.grandtotal;

    const paymentRows = [];

    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    const cashAmt = CurrencyConverter.toCurrency(cashPaid || 0, "INR");
    const cardAmt = CurrencyConverter.toCurrency(cardPaid || 0, "INR");
    const onlineAmt = CurrencyConverter.toCurrency(onlinePaid || 0, "INR");
    const upiAmt = CurrencyConverter.toCurrency(upiPaid || 0, "INR");
    const balanceAmt = CurrencyConverter.toCurrency(balanceDue || 0, "INR");

    // condition based rows
    if (globalData?.cashPaid > 0) {
      paymentRows.push(["Cash", `₹${globalData?.cashPaid}`]);
    }

    if (globalData?.cardPaid > 0) {
      paymentRows.push(["Card", `₹${globalData?.cardPaid}`]);
    }

    if (onlineAmt > 0) {
      paymentRows.push(["Online Amount", `₹${globalData?.onlinePaid}`]);
    }

    if (upiAmt > 0) {
      paymentRows.push(["UPI", `₹${globalData?.upiPaid}`]);
    }

    if (balanceAmt > 0) {
      paymentRows.push(["Balance Due", `₹${globalData?.balanceDue}`]);
    }

    let leftY = doc.lastAutoTable.finalY + 4;
    let rightY = doc.lastAutoTable.finalY + 4;

    const valueX = pageW - 8;
    const labelX = valueX - 25;
    rightY = ensureSpace(doc, rightY, 35);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(9);

    // Total Taxable
    doc.text("Total Amount Before Tax :", labelX, rightY, { align: "right" });
    doc.text(`₹${linetotalbaseprice}`, valueX, rightY, {
      align: "right",
    });
    rightY += 5;

    // GST
    if (isInterState) {
      doc.text("IGST :", labelX, rightY, { align: "right" });
      doc.text(`₹${igst}`, valueX, rightY, { align: "right" });
      rightY += 5;
    } else {
      doc.text("SGST :", labelX, rightY, { align: "right" });
      doc.text(`₹${sgst}`, valueX, rightY, { align: "right" });
      rightY += 5;

      doc.text("CGST :", labelX, rightY, { align: "right" });
      doc.text(`₹${cgst}`, valueX, rightY, { align: "right" });
      rightY += 5;
    }

    // Grand Total
    doc.setFont("Inter-Bold", "bold");
    doc.text("Total Amount :", labelX, rightY, { align: "right" });
    doc.text(`₹${grantotal}`, valueX, rightY, {
      align: "right",
    });
    rightY += 8;

    rightY = ensureSpace(doc, rightY, 25);
    const signBlockY = rightY;

    if (globalData?.storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + globalData?.storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);
        const maxWidth = 40;
        const maxHeight = 10;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;
        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          pageW - 8 - imgW,
          signBlockY,
          imgW,
          imgH
        );
      } catch {
        console.warn("Signature load failed");
      }
    }

    doc.setFont("Inter-Regular", "normal");
    doc.text("Issued By, Signature", pageW - 8, signBlockY + 14, {
      align: "right",
    });

    doc.setFont("Inter-Bold", "bold");
    doc.text(globalData?.salefrom?.title || "", pageW - 8, signBlockY + 18, {
      align: "right",
    });

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(9);
    doc.text("Total Items :", leftBoxX, leftY);
    doc.text(String(globalData?.totalItems), leftBoxX + 20, leftY);
    leftY += 4;

    leftY = ensureSpace(doc, leftY, 30);
    if (paymentRows.length > 0) {
      autoTable(doc, {
        startY: leftY,
        head: [["Payment Mode", ""]],
        body: paymentRows,
        margin: { left: 8 },
        tableWidth: 50,
        styles: {
          font: "Inter-Regular",
          fontSize: 8,
          cellPadding: 1,
          lineColor: selectedTheme?.templateTheme?.borderColor,
          lineWidth: 0.2,
          textColor: "#000",
        },
        headStyles: {
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          textColor: selectedTheme?.templateTheme?.foregroundColor,
          font: "Inter-Bold",
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 30, halign: "left" },
          1: { cellWidth: 20, halign: "right" },
        },
        theme: "grid",
      });

      leftY = doc.lastAutoTable.finalY + 5;
    }

    if (terms?.length > 0) {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW / 2 - 12);

      const termsHeight = termLines.length * 3.5 + 6;

      leftY = ensureSpace(doc, leftY, termsHeight);

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.text("Terms & Conditions", leftBoxX, leftY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.text(termLines, leftBoxX, leftY + 4);

      leftY += termsHeight;
    }

    // draw table ONLY if rows exist

    if (globalData?.storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW - 5;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");
      const boldW = doc.getTextWidth(boldText);

      const totalW = normalW + boldW;

      // Start X so whole string is right-aligned
      const startX = rightX - totalW;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y);

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + normalW, y);
    }
    setDownload(false);
    return doc;
  };

  const generateBill1 = async () => {
    const doc = new jsPDF("p", "mm", "a4");

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const ensureSpace = (
      doc,
      currentY,
      requiredHeight,
      topMargin = 10,
      bottomMargin = 10
    ) => {
      const pageH = doc.internal.pageSize.getHeight();
      if (currentY + requiredHeight > pageH - bottomMargin) {
        doc.addPage();
        return topMargin;
      }
      return currentY;
    };

    const drawLabelValue = (
      doc,
      label,
      value,
      x,
      y,
      maxWidth,
      lh,
      labelBold = true // 👈 default bold
    ) => {
      if (!value) return y;

      const labelText = `${label}: `;

      doc.setFont(
        labelBold ? "Inter-Bold" : "Inter-Regular",
        labelBold ? "bold" : "normal"
      );

      const labelWidth = doc.getTextWidth(labelText);
      doc.text(labelText, x, y);

      doc.setFont("Inter-Regular", "normal");
      const wrapped = doc.splitTextToSize(value, maxWidth - labelWidth);
      doc.text(wrapped, x + labelWidth, y);

      return y + wrapped.length * lh;
    };

    /* -------------------------------------------------
     HEADER (AUTO HEIGHT)
  ------------------------------------------------- */
    const borderColor = "#C0C0C0";
    const headerY = 8;

    const leftBoxX = 8;
    const leftBoxW = 110; // ⬅️ reduced width
    const rightBoxX = leftBoxX + leftBoxW + 2;
    const rightBoxW = pageW - rightBoxX - 8;

    const titleHeight = 8;
    const lh = 3.8;

    const headerPaddingTop = 2;
    const headerPaddingBottom = 1;

    const contentX = leftBoxX + 32;
    const maxTextWidth = leftBoxW - 34;

    const address = joinAddress(
      data?.salefrom?.addressline1,
      data?.salefrom?.addressline2,
      data?.salefrom?.district,
      data?.salefrom?.state,
      data?.salefrom?.pincode
    );

    let tempHeight = headerPaddingTop + titleHeight;

    // company name font size effect
    if (address) {
      tempHeight += doc.splitTextToSize(address, maxTextWidth).length * lh;
    }

    [
      data?.salefrom?.phone,
      data?.salefrom?.email,
      data?.salefrom?.gstnumber,
      data?.salefrom?.fssai,
      data?.salefrom?.cin,
      data?.salefrom?.pannumber,
    ].forEach((v) => {
      if (v) tempHeight += lh;
    });

    tempHeight += headerPaddingBottom;
    const logoBlockHeight = data?.salefrom?.image
      ? headerPaddingTop + 18 + headerPaddingBottom
      : 0;

    const metaRowHeight = 6; // each row height
    const metaTopSpacing = 5; // space from top before first row
    const metaRows = 5; // Invoice#, Date, POS, Due Date, Sales Person
    const rightMinHeight = metaTopSpacing + metaRows * metaRowHeight + 1;

    // ---- Final Header Height ---- //
    const headerH = Math.max(tempHeight, logoBlockHeight, rightMinHeight) - 1;

    // LEFT HEADER BOX
    const hexToRgb = (hex) => {
      if (!hex) return [0, 0, 0];
      const h = hex.replace("#", "");
      return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
      ];
    };

    const [r, g, b] = hexToRgb(borderColor);

    // Apply border color
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.3);
    doc.rect(leftBoxX, headerY, leftBoxW, headerH);

    // LOGO

    if (data?.salefrom?.image) {
      try {
        const imagePath = uRLs.IMAGE_URL + "profile/" + data.salefrom.image;

        // Convert to base64
        const base64 = await urlToBase64(imagePath);

        // Natural dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 🔥 Max logo area inside header box
        const maxWidth = 26;
        const maxHeight = headerH - 10;

        let imgW = width;
        let imgH = height;

        const ratio = Math.min(maxWidth / imgW, maxHeight / imgH);
        imgW *= ratio;
        imgH *= ratio;

        // Detect format
        const ext = imagePath.split(".").pop().toLowerCase();
        const format =
          ext === "png"
            ? "PNG"
            : ext === "jpg" || ext === "jpeg"
            ? "JPEG"
            : "JPEG";

        // Draw image
        doc.addImage(base64, format, leftBoxX + 2, headerY + 5, imgW, imgH);
      } catch (err) {
        console.warn("Logo load failed:", err);
      }
    }

    // COMPANY NAME
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text(
      data?.salefrom?.title || "",
      contentX,
      headerY + headerPaddingTop + 4
    );

    // COMPANY DETAILS
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);

    let cty = headerY + headerPaddingTop + titleHeight;
    if (address) {
      doc.setFont("Inter-Regular", "normal");
      const addrLines = doc.splitTextToSize(address, maxTextWidth);
      doc.text(addrLines, contentX, cty);
      cty += addrLines.length * lh;
    }

    // CONTACT & TAX INFO
    cty = drawLabelValue(
      doc,
      "Phone",
      data?.salefrom?.phone,
      contentX,
      cty,
      maxTextWidth,
      lh,
      false // 👈 normal
    );

    cty = drawLabelValue(
      doc,
      "Email",
      data?.salefrom?.email,
      contentX,
      cty,
      maxTextWidth,
      lh,
      false // 👈 normal
    );

    cty = drawLabelValue(
      doc,
      "GST",
      data?.salefrom?.gstnumber,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    cty = drawLabelValue(
      doc,
      "FSSAI",
      data?.salefrom?.fssai,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    cty = drawLabelValue(
      doc,
      "CIN",
      data?.salefrom?.cin,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    cty = drawLabelValue(
      doc,
      "PAN",
      data?.salefrom?.pannumber,
      contentX,
      cty,
      maxTextWidth,
      lh,
      true
    );

    // RIGHT HEADER BOX
    doc.rect(rightBoxX, headerY, rightBoxW, headerH);
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);

    // Decide title text
    const invoiceText =
      data.status === "CANCELLED"
        ? "CANCELLED"
        : data.salemode
        ? formatTrackMode(data.salemode) // eg: TAX INVOICE
        : "";

    // Optional: make CANCELLED stand out
    if (data.status === "CANCELLED") {
      doc.setTextColor(200, 0, 0); // red
    }

    doc.text(invoiceText, rightBoxX + rightBoxW - 2, headerY + 3, {
      align: "right",
    });

    // reset color for rest of document
    doc.setTextColor(0, 0, 0);

    const invDate = DateConverter.ISOToDisplayFormat(data?.saledate).date;

    const meta = [
      ["Bill No", data.saleno],
      ["Date", invDate],
      ["Place Of Supply", data.placeofsupply || "Tamil Nadu"],
      ["Due Date", invDate],
      ["Sales Person", data.salesperson || ""],
    ];

    let my = headerY + 5;
    meta.forEach(([k, v]) => {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text(k, rightBoxX + 2, my);
      doc.text(":", rightBoxX + 30, my);

      doc.setFont("Inter-Regular", "normal");
      doc.text(String(v || "-"), rightBoxX + 34, my);
      my += 6;
    });

    /* -------------------------------------------------
     CUSTOMER & SHIPPING (AUTO HEIGHT)
  ------------------------------------------------- */
    const pagePadding = 8;
    const gapBetweenBoxes = 2;
    const innerPadding = 2;
    const titleGap = 4; // space below title

    // total available width
    const availableWidth = pageW - pagePadding * 2 - gapBetweenBoxes;
    const boxWidth = availableWidth / 2;

    //  separate variables (no conflict with header)
    const custBoxX = pagePadding;
    const custBoxW = boxWidth;

    const shipBoxX = custBoxX + custBoxW + gapBetweenBoxes;
    const shipBoxW = boxWidth;

    const boxY = headerY + headerH + 2;
    let tableStartY;

    const hasSaleTo =
      data?.saleto &&
      (data.saleto.name ||
        data.saleto.addressline1 ||
        data.saleto.addressline2 ||
        data.saleto.district ||
        data.saleto.state ||
        data.saleto.pincode ||
        data.saleto.gstnumber);

    if (hasSaleTo ) {
      const lineHeight = 3;

      const innerTextWidthLeft = custBoxW - 4;
      const innerTextWidthRight = shipBoxW - 4;

      // ---------- CUSTOMER TEXT
      const customerText = [
        { label: null, value: data?.saleto?.name },
        {
          label: null,
          value: joinAddress(
            data?.saleto?.addressline1,
            data?.saleto?.addressline2
          ),
        },
        {
          label: null,
          value: `${data?.saleto?.district}, ${data?.saleto?.state} - ${data?.saleto?.pincode}`,
        },
        data?.saleto?.phone && { label: "Phone", value: data.saleto.phone },
        data?.saleto?.email && { label: "Email", value: data.saleto.email },
        data?.saleto?.gstnumber && {
          label: "GSTIN",
          value: data.saleto.gstnumber,
        },
        data?.saleto?.pannumber && {
          label: "PAN",
          value: data.saleto.pannumber,
        },
        data?.saleto?.cinnumber && {
          label: "CIN",
          value: data.saleto.cinnumber,
        },
      ].filter(Boolean);

      let customerLineCount = 0;
      customerText.forEach((t) => {
        customerLineCount += wrapText(doc, t, innerTextWidthLeft).length;
      });

      // ---------- SHIPPING TEXT
      const shippingText = [
        data?.saleto?.name,
        joinAddress(data?.saleto?.addressline1, data?.saleto?.addressline2),
        `${data?.saleto?.district}, ${data?.saleto?.state}`,
        data?.transport || "",
      ].filter(Boolean);

      let shippingLines = [];
      shippingText.forEach((t) => {
        shippingLines.push(...wrapText(doc, t, innerTextWidthRight));
      });
      const shippingLineCount = shippingLines.length;

      // ---------- HEIGHT CALCULATION ----//
      const contentStartOffset = innerPadding + 2 + titleGap;

      const custBoxH = contentStartOffset + customerLineCount * lineHeight + 2;

      const shipBoxH = contentStartOffset + shippingLineCount * lineHeight + 2;

      const boxH = Math.max(custBoxH, shipBoxH);

      // ---------- CUSTOMER BOX

      doc.rect(custBoxX, boxY, custBoxW, boxH);

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.text(
        "Customer Details",
        custBoxX + innerPadding,
        boxY + innerPadding + 3
      );

      let cy = boxY + innerPadding + 3 + titleGap;
      const boldLabels = ["GSTIN", "PAN", "CIN"];

      customerText.forEach((item, idx) => {
        if (!item.label) {
          doc.setFont(
            idx === 0 ? "Inter-Bold" : "Inter-Regular",
            idx === 0 ? "bold" : "normal"
          );
          // name / address
          const lines = wrapText(doc, item.value, innerTextWidthLeft);
          lines.forEach((line) => {
            doc.text(line, custBoxX + innerPadding, cy);
            cy += lineHeight;
          });
        } else {
          const isBold = boldLabels.includes(item.label);

          // label
          doc.setFont(
            isBold ? "Inter-Bold" : "Inter-Regular",
            isBold ? "bold" : "normal"
          );
          doc.text(`${item.label} :`, custBoxX + innerPadding, cy);

          const labelW = doc.getTextWidth(`${item.label} : `);

          // value always normal
          doc.setFont("Inter-Regular", "normal");
          doc.text(item.value, custBoxX + innerPadding + labelW, cy);

          cy += lineHeight;
        }
      });

      // ---------- SHIPPING BOX
      doc.rect(shipBoxX, boxY, shipBoxW, boxH);

      doc.setFont("Inter-Bold", "bold");
      doc.text(
        "Shipping Address",
        shipBoxX + innerPadding,
        boxY + innerPadding + 3
      );

      doc.setFont("Inter-Regular", "normal");
      let sy = boxY + innerPadding + 4 + titleGap;

      shippingLines.forEach((line) => {
        doc.text(line, shipBoxX + innerPadding, sy);
        sy += lineHeight;
      });

      tableStartY = boxY + boxH + 2;
    } else {
      tableStartY = boxY;
    }

    /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
    autoTable(doc, {
      startY: tableStartY,
      theme: "grid",
      margin: { left: 8, right: 5 },
      styles: {
        font: "Inter-Regular",
        fontSize: 7,
        cellPadding: 1,
        valign: "middle",
        overflow: "linebreak",
        lineWidth: 0.2,
        textColor: "#000000",
      },
      headStyles: {
        font: "Inter-Bold",
        fontSize: 7,
        halign: "center",
        valign: "middle",
        cellPadding: 0.8,
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
      },
      head: [
        [
          "S.No",
          "Description",
          "HSN/SAC",
          "MRP",
          "Discount",
          "Rate",
          "Qty",
          "Amount",
        ],
      ],
      body: data.saleitems.map((it, i) => {
        const amount = CurrencyConverter.toCurrency(
          it.lineGrandTotal || 0,
          "INR"
        );
        const mrp = CurrencyConverter.toCurrency(it.mrp || 0, "INR");
        const Discount = CurrencyConverter.toCurrency(it.discount || 0, "INR");
        const rate = CurrencyConverter.toCurrency(it.soldprice || 0, "INR");

        return [
          i + 1,
          [it.title, it.note ? `-(${it.note})` : ""].filter(Boolean),
          it.hsnsac || "",
          `₹${formatCurrency(mrp)}`,
          `₹${formatCurrency(Discount)}`,
          `₹${formatCurrency(rate)}`,
          `${it.quantity} Pcs`,
          `₹${formatCurrency(amount)}`,
        ];
      }),
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 83, halign: "left" },
        2: { cellWidth: 16, halign: "center" },
        3: { cellWidth: 18, halign: "right" },
        4: { cellWidth: 18, halign: "right" },
        5: { cellWidth: 18, halign: "right" },
        6: { cellWidth: 12, halign: "center" },
        7: { cellWidth: 18, halign: "right" },
      },
    });

    /* -------------------------------------------------
     TOTALS
  ------------------------------------------------- */
    const grantotal = CurrencyConverter.toCurrency(data.grandtotal || 0, "INR");

    const paymentRows = [];

    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    const cashAmt = CurrencyConverter.toCurrency(cashPaid || 0, "INR");
    const cardAmt = CurrencyConverter.toCurrency(cardPaid || 0, "INR");
    const onlineAmt = CurrencyConverter.toCurrency(onlinePaid || 0, "INR");
    const upiAmt = CurrencyConverter.toCurrency(upiPaid || 0, "INR");
    const balanceAmt = CurrencyConverter.toCurrency(balanceDue || 0, "INR");

    // condition based rows
    if (cashAmt > 0) {
      paymentRows.push([
        "Cash",
        `₹${formatCurrency(cashAmt, "INR", false, true)}`,
      ]);
    }

    if (cardAmt > 0) {
      paymentRows.push([
        "Card",
        `₹${formatCurrency(cardAmt, "INR", false, true)}`,
      ]);
    }

    if (onlineAmt > 0) {
      paymentRows.push([
        "Online Amount",
        `₹${formatCurrency(onlineAmt, "INR", false, true)}`,
      ]);
    }

    if (upiAmt > 0) {
      paymentRows.push([
        "UPI",
        `₹${formatCurrency(upiAmt, "INR", false, true)}`,
      ]);
    }

    if (balanceAmt > 0) {
      paymentRows.push([
        "Balance Due",
        `₹${formatCurrency(balanceAmt, "INR", false, true)}`,
      ]);
    }

    let leftY = doc.lastAutoTable.finalY + 4;
    let rightY = doc.lastAutoTable.finalY + 4;

    const valueX = pageW - 8;
    const labelX = valueX - 25;
    rightY = ensureSpace(doc, rightY, 35);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(9);
    // Grand Total
    doc.setFont("Inter-Bold", "bold");
    doc.text("Total Amount :", labelX, rightY, { align: "right" });
    doc.text(`₹${formatCurrency(grantotal)}`, valueX, rightY, {
      align: "right",
    });
    rightY += 8;

    rightY = ensureSpace(doc, rightY, 25);
    const signBlockY = rightY;

    if (globalData?.storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + globalData?.storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);
        const maxWidth = 40;
        const maxHeight = 10;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;
        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          pageW - 8 - imgW,
          signBlockY,
          imgW,
          imgH
        );
      } catch {
        console.warn("Signature load failed");
      }
    }

    doc.setFont("Inter-Regular", "normal");
    doc.text("Issued By, Signature", pageW - 8, signBlockY + 14, {
      align: "right",
    });

    doc.setFont("Inter-Bold", "bold");
    doc.text(data?.salefrom?.title || "", pageW - 8, signBlockY + 18, {
      align: "right",
    });

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(9);
    doc.text("Total Items :", leftBoxX, leftY);
    doc.text(String(data.totalItems), leftBoxX + 20, leftY);
    leftY += 4;

    leftY = ensureSpace(doc, leftY, 30);
    if (paymentRows.length > 0) {
      autoTable(doc, {
        startY: leftY,
        head: [["Payment Mode", ""]],
        body: paymentRows,
        margin: { left: 8 },
        tableWidth: 50,
        styles: {
          font: "Inter-Regular",
          fontSize: 8,
          cellPadding: 1,
          lineColor: selectedTheme?.templateTheme?.borderColor,
          lineWidth: 0.2,
          textColor: "#000",
        },
        headStyles: {
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          textColor: selectedTheme?.templateTheme?.foregroundColor,
          font: "Inter-Bold",
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 30, halign: "left" },
          1: { cellWidth: 20, halign: "right" },
        },
        theme: "grid",
      });

      leftY = doc.lastAutoTable.finalY + 5;
    }

    if (terms?.length > 0) {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW / 2 - 12);

      const termsHeight = termLines.length * 3.5 + 6;

      leftY = ensureSpace(doc, leftY, termsHeight);

      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.text("Terms & Conditions", leftBoxX, leftY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.text(termLines, leftBoxX, leftY + 4);

      leftY += termsHeight;
    }

    // draw table ONLY if rows exist

    if (globalData?.storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW - 5;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");
      const boldW = doc.getTextWidth(boldText);

      const totalW = normalW + boldW;

      // Start X so whole string is right-aligned
      const startX = rightX - totalW;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y);

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + normalW, y);
    }
    setDownload(false);
    return doc;
  };

  const handleDownload = async (data) => {
    try {
      setDownload(true);

      const { salemode, saleno } = data || {};
      let doc;

      if (salemode?.toUpperCase() === "BILL") {
        doc = await generateBill1();
      } else {
        doc = await generateInvoice1();
      }
      const filenamePrefix = formatTrackMode(salemode)?.replace(/\s+/g, "_");
      doc.save(`${filenamePrefix}_${saleno}.pdf`);
      setTimeout(() => {
        window.close();
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownload(false);
    }
  };

  const handlePrint = async (data, overrideSize = null) => {
    setPrint(true);
    try {
      const upperMode = data?.salemode?.toUpperCase();
      let doc;

      if (upperMode === "BILL") {
        doc = await generateBill1();
      } else {
        doc = await generateInvoice1();
      }

      const blob = doc.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        const printWindow = iframe.contentWindow;

        const cleanup = () => {
          // Remove iframe safely
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }

          // Remove listeners
          window.removeEventListener("afterprint", cleanup);
          printWindow.matchMedia("(print)").removeListener(handlePrintState);
        };

        const handlePrintState = (mql) => {
          // WHEN PRINT PREVIEW CLOSES (printed OR canceled)
          if (!mql.matches) {
            setTimeout(cleanup, 50); // small delay makes Chrome stable
          }
        };

        // Watch print state
        printWindow.matchMedia("(print)").addListener(handlePrintState);

        // Also catch Chrome's afterprint
        window.addEventListener("afterprint", cleanup);
        setPrint(false);
        setTimeout(() => {
          if (window.bootstrap && window.bootstrap.Dropdown) {
            const dropdownElementList = [].slice.call(
              document.querySelectorAll(".dropdown-toggle")
            );
            dropdownElementList.map(function (dropdownToggleEl) {
              return new window.bootstrap.Dropdown(dropdownToggleEl);
            });
          }
        }, 100);
        // Trigger printing
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 300);
      };
    } catch (error) {
      setPrint(false);
      handleError(error, showAlert);
      return false;
    }
    // finally {
    //   setPrint(false);
    // }
  };

  useEffect(() => {
    jsPDF.API.events.push([
      "addFonts",
      function () {
        this.addFileToVFS("Inter_24pt-Regular-normal.ttf", InterRegular);
        this.addFileToVFS("Inter_24pt-Bold-normal.ttf", InterBold);
        this.addFont(
          "Inter_24pt-Regular-normal.ttf",
          "Inter-Regular",
          "normal"
        );
        this.addFont("Inter_24pt-Bold-normal.ttf", "Inter-Bold", "bold");
      },
    ]);
  }, []);

  useEffect(() => {
    if (data?.saleno) {
      handleDownload(data);
    }
  }, [data?.saleno]);



 

  useEffect(() => {
    setData(isData);
  }, [isData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "p") {
        event.preventDefault();
        handlePrint(data);
      }
    };
    const handleAfterPrint = () => {
      setTimeout(() => {
        window.focus();
      }, 100);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [data]);



  return (
    <>
                   <InvoiceLayout>
                       {globalData.salemode === "INVOICE" || isBillReceipt? (
                        <>                       
                          <InvoiceWrapper>
                                  <div className="tm_invoice_in p-2">
                                    <div className="row">
                                      <div className="col-12 col-md-7 pe-1 ">
                                        <div className="border p-2 h-100 d-flex ">
                                          <div className="d-flex gap-2 align-items-start">
                                            <div className="tm_logo">
                                              {globalData?.salefrom?.image && (
                                                <img
                                                  src={
                                                    uRLs.IMAGE_URL +
                                                    "profile/" +
                                                    globalData?.salefrom?.image
                                                  }
                                                  className="img-rounded logo mb-0"
                                                  height="45"
                                                  width="100"
                                                  style={{
                                                    objectFit: "contain",
                                                  }}
                                                />
                                              )}
                                            </div>
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "600",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                {globalData?.salefrom?.title ||
                                                  ""}
                                              </div>
                                              <div>
                                                {globalData?.salefrom
                                                  ?.addressline1 || ""}{" "}
                                                {globalData?.salefrom
                                                  ?.addressline2 || ""}{" "}
                                              </div>
                                              <div>
                                                {globalData?.salefrom?.state ||
                                                  ""}{" "}
                                                {globalData?.salefrom
                                                  ?.district || ""}{" "}
                                                {globalData?.salefrom
                                                  ?.country || ""}{" "}
                                                -{" "}
                                                {globalData?.salefrom
                                                  ?.pincode || ""}
                                              </div>
                                              <div>
                                                CIN :
                                                {
                                                  globalData?.salefrom
                                                    ?.cinnumber
                                                }
                                              </div>
                                              {globalData?.salefrom
                                                ?.pannumber && (
                                                <div>
                                                  <strong>PAN:</strong>{" "}
                                                  {globalData?.salefrom
                                                    ?.pannumber || ""}
                                                </div>
                                              )}
                                              <div>
                                                {" "}
                                                Phone: +91{" "}
                                                {globalData?.salefrom?.phone ||
                                                  ""}
                                              </div>
                                              {globalData?.salefrom
                                                ?.gstnumber && (
                                                <div>
                                                  <strong>GSTIN:</strong>{" "}
                                                  {globalData?.salefrom
                                                    ?.gstnumber || ""}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-12 col-md-5 ps-md-1 mt-2 mt-md-0">
                                        <div className="border h-100 p-2">
                                          <div className="tm_text_uppercase text-end">
                                            {globalData?.salemode &&
                                             <>{documentTitle}</>
                                              }
                                          </div>
                                          <div>
                                            <b>{invLabel}</b> {" "}
                                            {globalData?.saleno}
                                          </div>
                                          <div>
                                            <b>Date</b> : {saleTime?.date}
                                          </div>
                                          <div>
                                            <b>Place Of Supply</b> :{" "}
                                            {globalData?.salefrom?.state || ""}
                                          </div>
                                          <div>
                                            <b>Due Date</b> : {saleTime?.date}
                                          </div>
                                          <div>
                                            <b>Sales Person</b> : -
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {globalData?.saleto  && (
                                      <div className="row mt-2">
                                        <div className="col-12 col-md-6 pe-1">
                                          <div className="border p-2 h-100 ">
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "600",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                Customer Details
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                {globalData?.saleto?.name || ""}
                                              </div>
                                              <div>
                                                {globalData?.saleto
                                                  ?.addressline1 || ""}{" "}
                                                {globalData?.saleto
                                                  ?.addressline2 || ""}{" "}
                                              </div>
                                              <div>
                                                {globalData?.saleto?.state ||
                                                  ""}{" "}
                                                {globalData?.saleto?.district ||
                                                  ""}{" "}
                                                {globalData?.saleto?.country ||
                                                  ""}{" "}
                                                -{" "}
                                                {globalData?.saleto?.pincode ||
                                                  ""}
                                              </div>
                                              <div>
                                                {" "}
                                                Phone: +91{" "}
                                                {globalData?.saleto?.phone ||
                                                  ""}
                                              </div>
                                              <div>
                                                {" "}
                                                Email{" "}
                                                {globalData?.saleto?.email ||
                                                  ""}
                                              </div>
                                              {globalData?.saleto
                                                ?.gstnumber && (
                                                <div>
                                                  <span>GSTIN:</span>{" "}
                                                  {globalData?.saleto
                                                    ?.gstnumber || ""}
                                                </div>
                                              )}
                                              {globalData?.saleto
                                                ?.pannumber && (
                                                <div>
                                                  <span>PAN:</span>{" "}
                                                  {globalData?.saleto
                                                    ?.pannumber || ""}
                                                </div>
                                              )}
                                              <div>
                                                CIN :
                                                {globalData?.saleto
                                                  ?.cinnumber || ""}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                           {globalData?.saleto?.shippingAddress
                                              ?.addressline1 &&
                                              globalData?.saleto
                                                ?.shippingAddress
                                                ?.addressline2 && (
                                        <div className="col-12 col-md-6 ps-md-1 mt-2 mt-md-0">
                                          <div className="border p-2 h-100 ">
                                         
                                                <div>
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      fontWeight: "600",
                                                      marginBottom: "0px",
                                                    }}
                                                  >
                                                    Shipping Address
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      marginBottom: "0px",
                                                    }}
                                                  >
                                                    {globalData?.saleto
                                                      ?.shippingAddress?.name ||
                                                      ""}
                                                  </div>
                                                  <div>
                                                    {globalData?.saleto
                                                      ?.shippingAddress
                                                      ?.addressline1 || ""}{" "}
                                                    {globalData?.saleto
                                                      ?.shippingAddress
                                                      ?.addressline2 || ""}{" "}
                                                  </div>
                                                  <div>
                                                    {globalData?.saleto
                                                      ?.shippingAddress
                                                      ?.state || ""}{" "}
                                                    {globalData?.saleto
                                                      ?.shippingAddress
                                                      ?.district || ""}{" "}
                                                    {
                                                      globalData?.saleto
                                                        ?.shippingAddress?.city
                                                    }{" "}
                                                    {globalData?.saleto
                                                      ?.shippingAddress
                                                      ?.country || ""}{" "}
                                                    -{" "}
                                                    {globalData?.saleto
                                                      ?.shippingAddress
                                                      ?.pincode || ""}
                                                  </div>
                                                  {/* <div>
                                    {" "}
                                    Phone: +91 {data.saleto?.phone || ""}
                                  </div>
                                  <div>
                                    {" "}
                                    Email {data.saleto?.email || ""}
                                  </div>
                                  {data.saleto?.gstnumber && (
                                    <div>
                                      <span>GSTIN:</span>{" "}
                                      {data.saleto?.gstnumber || ""}
                                    </div>
                                  )}
                                   {data.saleto?.pannumber && (
                                    <div>
                                      <span>PAN:</span>{" "}
                                      {data.saleto?.pannumber || ""}
                                    </div>
                                  )}
                                  <div>CIN :</div> */}
                                                </div>
                                             
                                          </div>
                                        </div>
                                         )}
                                      </div>
                                    )}
                                      <div
                                      style={{
                                        width: "100%",
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                      }}
                                    >
                                    <table
                                      // className="items"
                                      width="100%"
                                      style={{
                                        marginTop: "6px",
                                        borderCollapse: "collapse",
                                      }}
                                      cellPadding="4"
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "20px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            S.No
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "240px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Description
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "120px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            HSN/SAC
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "50px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            MRP
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Base Price
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Qty
                                          </th>

                                          <th
                                            className="force-style"
                                            style={{
                                              width: "30px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Taxable Value
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Tax
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Tax Amount
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              width: "100px",
                                              textAlign: "center",
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                            }}
                                          >
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {globalData?.saleitems?.map(
                                          (product, index) => (
                                            <tr key={index}>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {index + 1}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "left",
                                                  fontSize: "12px",
                                                  width: "30%",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                <div>{product?.title}</div>
                                         
                                                {product.imode === "PRODUCT" &&
                                                  product.note && (
                                                    <div className="ms-0 fsz-10 mb-1">
                                                      {product?.note}
                                                    </div>
                                                  )}
                                             
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {product?.hsnsac}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product?.mrp}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product?.baseprice}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {product?.quantity}
                                                <span className="font-11 ms-1">
                                                  {(() => {
                                                    const unitCode =
                                                      product?.isSecondaryUnit
                                                        ? product?.unitConfig
                                                            ?.secondaryUnitCode
                                                        : product?.unitConfig
                                                            ?.primaryUnitCode;

                                                    // return unitCode !== "unit" ? unitCode : "";
                                                    return unitCode || "";
                                                  })()}
                                                </span>
                                              </td>

                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product?.lineTotalBasePrice}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {`${product?.taxpercentage}%`}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product?.lineTotalTaxAmount}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product?.lineGrandTotal}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                        <tr>
                                          <td colSpan="6">
                                            Total Items :{" "}
                                            {globalData?.saleitems?.length}
                                          </td>
                                          <td colSpan="3">
                                            Total Amount Before Tax :
                                          </td>
                                          <td
                                            style={{
                                              textAlign: "end",
                                              fontSize: "12px",
                                              fontWeight: 500,
                                            }}
                                          >
                                            ₹{globalData?.totalbaseprice}{" "}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td
                                            colSpan="6"
                                            style={{ verticalAlign: "top" }}
                                          >
                                            {globalData?.grandtotal > 0 && (
                                              <table
                                                className="items"
                                                width="50%"
                                                cellPadding="4"
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      colSpan={2}
                                                      className="force-style"
                                                      style={{
                                                        width: "100%",
                                                        textAlign: "center",
                                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        "--th-bg":
                                                          selectedTheme
                                                            ?.templateTheme
                                                            ?.primaryColor,
                                                        "--th-color":
                                                          selectedTheme
                                                            ?.templateTheme
                                                            ?.foregroundColor,
                                                      }}
                                                    >
                                                      Payment Mode
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {(globalData?.cashamount ||
                                                    0) -
                                                    (globalData?.cashchange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        Cash
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          fontWeight: "600",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹{globalData?.cashPaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.cardamount ||
                                                    0) -
                                                    (globalData?.cardchange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        Card
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          fontWeight: "600",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹{globalData?.cardPaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.onlineamount ||
                                                    0) -
                                                    (globalData?.onlinechange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        OnlineAmount
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          fontWeight: "600",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹
                                                        {globalData?.onlinePaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.upiamount ||
                                                    0) -
                                                    (globalData?.upichange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        UPI
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          fontWeight: "600",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹{globalData?.upiPaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.paylateramount ||
                                                    0) -
                                                    (globalData?.paylaterchange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        BalanceDue
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          fontWeight: "600",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹
                                                        {globalData?.balanceDue}
                                                      </td>
                                                    </tr>
                                                  )}
                                                </tbody>
                                              </table>
                                            )}
                                          </td>
                                          <td
                                            colSpan="3"
                                            style={{ verticalAlign: "top" }}
                                          >
                                            {globalData?.salefrom?.statecode &&
                                            globalData?.saleto?.statecode &&
                                            globalData?.salefrom?.statecode !==
                                              globalData?.saleto?.statecode ? (
                                              <div>IGST</div>
                                            ) : (
                                              <>
                                                <div>SGST :</div>
                                                <div>CGST :</div>
                                              </>
                                            )}
                                            <div
                                              style={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Total Amount :
                                            </div>
                                          </td>
                                          <td
                                            style={{
                                              textAlign: "end",
                                              fontSize: "12px",
                                              verticalAlign: "top",
                                            }}
                                          >
                                            {globalData?.salefrom?.statecode &&
                                            globalData?.saleto?.statecode &&
                                            globalData?.salefrom?.statecode !==
                                              globalData?.saleto?.statecode ? (
                                              <div>
                                                ₹{globalData?.igstamount}
                                              </div>
                                            ) : (
                                              <>
                                                <div>
                                                  {" "}
                                                  {globalData?.sgstamount}
                                                </div>
                                                <div>
                                                  ₹{globalData?.cgstamount}
                                                </div>
                                              </>
                                            )}
                                            <div
                                              style={{
                                                textAlign: "end",
                                                fontSize: "14px",
                                                fontWeight: 600,
                                              }}
                                            >
                                              ₹{globalData?.grandtotal}
                                            </div>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    </div>
                                     <div className="row mt-2">
                                    {terms?.length > 0 && (
                                      <div className="col-12 col-md-8 mb-3 mb-md-0">
                                        <div
                                          className="fs-13 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box">{terms}</div>
                                      </div>
                                    )}

                                    <div className="col-12 col-md-4 d-flex justify-content-end ">
                                      <div>
                                        {globalData?.storeSettings?.invoiceSettings?.signature && (
                                          <div className="text-end p-0 fw-bold">
                                            <img
                                              src={
                                                uRLs.IMAGE_URL +
                                                globalData?.storeSettings?.invoiceSettings?.signature
                                              }
                                              width={150}
                                              alt="Signature"
                                            />
                                          </div>
                                        )}

                                        <div className="text-end p-0">
                                          Issued By, Signature
                                        </div>

                                        <div className="text-end p-0 fw-bold">
                                          {globalData?.salefrom?.title}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                    {/* <table
                            width="100%"
                            style={{ borderCollapse: "collapse" }}
                            cellPadding="8"
                          >
                            <tbody>
                            <tr>
                            {globalData?.storeSettings?.invoiceSettings?.signature && (
                                  <td className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        globalData?.storeSettings?.invoiceSettings?.signature
                                      }
                                      width={150}
                                    />
                                  </td> )}
          
                              </tr>
                              <tr>
                                <td className="text-end p-0">Issued By,Signature</td>
                              </tr>
                              <tr>
                                <td className="text-end p-0 fw-bold ">
                                  {settings?.saleProfileData?.title}
                                </td>
                              </tr>
                            </tbody>
                          </table> */}
                                  </div>
                              </InvoiceWrapper>
                         
                        </>
                      ) : globalData.salemode === "BILL" ? (
                        <>
                          
                            <InvoiceWrapper>
                                  <div className="tm_invoice_in p-2">
                                    <div className="row">
                                      <div className="col-12 col-md-7pe-1 ">
                                        <div className="border p-2 h-100 d-flex ">
                                          <div className="d-flex gap-2 align-items-start">
                                            <div className="tm_logo">
                                              {globalData?.salefrom?.image && (
                                                <img
                                                  src={
                                                    uRLs.IMAGE_URL +
                                                    "profile/" +
                                                    globalData?.salefrom?.image
                                                  }
                                                  className="img-rounded logo mb-0"
                                                  height="45"
                                                  width="150"
                                                  style={{
                                                    objectFit: "contain",
                                                  }}
                                                />
                                              )}
                                            </div>
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "600",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                {globalData.salefrom?.title ||
                                                  ""}
                                              </div>
                                              <div>
                                                {globalData?.salefrom
                                                  ?.addressline1 || ""}{" "}
                                                {globalData?.salefrom
                                                  ?.addressline2 || ""}{" "}
                                              </div>
                                              <div>
                                                {globalData?.salefrom?.state ||
                                                  ""}{" "}
                                                {globalData?.salefrom
                                                  ?.district || ""}{" "}
                                                {globalData?.salefrom
                                                  ?.country || ""}{" "}
                                                -{" "}
                                                {globalData?.salefrom
                                                  ?.pincode || ""}
                                              </div>
                                              {globalData?.salefrom
                                                ?.pannumber && (
                                                <div>
                                                  <strong>PAN:</strong>{" "}
                                                  {globalData?.salefrom
                                                    ?.pannumber || ""}
                                                </div>
                                              )}
                                              <div>
                                                {" "}
                                                Phone:{" "}
                                                {globalData?.salefrom?.phone ||
                                                  ""}
                                              </div>
                                              <div>
                                                {" "}
                                                Email:{" "}
                                                {globalData?.salefrom?.email ||
                                                  ""}
                                              </div>
                                              {globalData?.salefrom
                                                ?.gstnumber && (
                                                <div>
                                                  <strong>GSTIN:</strong>{" "}
                                                  {globalData?.salefrom
                                                    ?.gstnumber || ""}
                                                </div>
                                              )}
                                              {globalData?.salefrom?.fssai && (
                                                <div>
                                                  <strong>FSSAI:</strong>{" "}
                                                  {globalData?.salefrom
                                                    ?.fssai || ""}
                                                </div>
                                              )}
                                              {globalData?.salefrom?.cin && (
                                                <div>
                                                  <strong>CIN:</strong>{" "}
                                                  {globalData?.salefrom?.cin ||
                                                    ""}
                                                </div>
                                              )}
                                              {globalData?.salefrom
                                                ?.pannumber && (
                                                <div>
                                                  <strong>PAN:</strong>{" "}
                                                  {globalData?.salefrom
                                                    ?.pannumber || ""}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-12 col-md-5 ps-1 ">
                                        <div className="border h-100 p-2">
                                          <div className="tm_text_uppercase text-end">
                                            {globalData.salemode &&
                                              formatTrackMode(
                                                globalData.salemode,
                                              )}
                                          </div>
                                          <div>
                                            <b>Bill #</b> : {globalData.saleno}
                                          </div>
                                          <div>
                                            <b>Date</b> : {saleTime.date}
                                          </div>
                                          <div>
                                            <b>Place Of Supply</b> :{" "}
                                            {globalData?.salefrom?.state || ""}
                                          </div>
                                          <div>
                                            <b>Due Date</b> : {saleTime.date}
                                          </div>
                                          <div>
                                            <b>Sales Person</b> : -
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {globalData?.saleto  && (
                                      <div className="row mt-2">
                                        <div className="col-12 col-md-6 pe-1">
                                          <div className="border p-2 h-100 ">
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "600",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                Customer Details
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                {globalData?.saleto?.name || ""}
                                              </div>
                                              <div>
                                                {globalData?.saleto
                                                  ?.addressline1 || ""}{" "}
                                                {globalData?.saleto
                                                  ?.addressline2 || ""}{" "}
                                              </div>
                                              <div>
                                                {globalData?.saleto?.state ||
                                                  ""}{" "}
                                                {globalData?.saleto?.district ||
                                                  ""}{" "}
                                                {globalData?.saleto?.country ||
                                                  ""}{" "}
                                                -{" "}
                                                {globalData?.saleto?.pincode ||
                                                  ""}
                                              </div>
                                              <div>
                                                {" "}
                                                Phone: +91{" "}
                                                {globalData?.saleto?.phone ||
                                                  ""}
                                              </div>
                                              {globalData?.saleto?.email && (
                                                <div>
                                                  {" "}
                                                  Email{" "}
                                                  {globalData?.saleto?.email ||
                                                    ""}
                                                </div>
                                              )}
                                              {globalData?.saleto
                                                ?.gstnumber && (
                                                <div>
                                                  <strong>GSTIN:</strong>{" "}
                                                  {globalData?.saleto
                                                    ?.gstnumber || ""}
                                                </div>
                                              )}
                                              {globalData?.saleto
                                                ?.pannumber && (
                                                <div>
                                                  <strong>PAN:</strong>{" "}
                                                  {globalData?.saleto
                                                    ?.pannumber || ""}
                                                </div>
                                              )}
                                              {globalData?.saleto?.cin && (
                                                <div>
                                                  <strong>CIN:</strong>{" "}
                                                  {globalData?.saleto?.cin ||
                                                    ""}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                         {(globalData?.saleto
                                              ?.shippingAddress?.addressline1 ||
                                              globalData?.saleto
                                                ?.shippingAddress
                                                ?.addressline2) && (
                                        <div className="col-12 col-md-6 ps-1">
                                          <div className="border p-2 h-100 ">                                           
                                              <div>
                                                <div
                                                  style={{
                                                    fontSize: "12px",
                                                    fontWeight: "600",
                                                    marginBottom: "0px",
                                                  }}
                                                >
                                                  Shipping Address
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: "12px",
                                                    marginBottom: "0px",
                                                  }}
                                                >
                                                  {
                                                    globalData?.saleto
                                                      ?.shippingAddress?.name
                                                  }
                                                </div>
                                                <div>
                                                  {globalData?.saleto
                                                    ?.shippingAddress
                                                    ?.addressline1 || ""}{" "}
                                                  {globalData?.saleto
                                                    ?.shippingAddress
                                                    ?.addressline2 || ""}{" "}
                                                </div>
                                                <div>
                                                  {globalData?.saleto
                                                    ?.shippingAddress?.state ||
                                                    ""}{" "}
                                                  {globalData?.saleto
                                                    ?.shippingAddress
                                                    ?.district || ""}{" "}
                                                  {
                                                    globalData?.saleto
                                                      ?.shippingAddress?.city
                                                  }{" "}
                                                  {globalData?.saleto
                                                    ?.shippingAddress
                                                    ?.country || ""}{" "}
                                                  -{" "}
                                                  {globalData?.saleto
                                                    ?.shippingAddress
                                                    ?.pincode || ""}
                                                </div>
                                                {/* <div>
                                    {" "}
                                    Phone: {data.saleto?.phone || ""}
                                  </div>
                                  {data.saleto?.gstnumber && (
                                    <div>
                                      <span>GSTIN:</span>{" "}
                                      {data.saleto?.gstnumber || ""}
                                    </div>
                                  )} */}
                                              </div>                                           
                                          </div>                                          
                                        </div>
                                         )}
                                      </div>
                                    )}
                                    <div
                                      style={{
                                        width: "100%",
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                      }}
                                    >
                                    <table
                                      // className="items"
                                      width="100%"
                                      style={{
                                        marginTop: "6px",
                                        borderCollapse: "collapse",
                                      }}
                                      cellPadding="4"
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            S.No
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            Description
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            HSN/SAC
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            MRP
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            Discount
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            Rate
                                          </th>
                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            Qty
                                          </th>

                                          <th
                                            className="force-style"
                                            style={{
                                              // width: "10px",
                                              textAlign: "center",
                                              //   background: "#f3f3f3",
                                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                              "--th-bg":
                                                selectedTheme?.templateTheme
                                                  ?.primaryColor,
                                              "--th-color":
                                                selectedTheme?.templateTheme
                                                  ?.foregroundColor,
                                            }}
                                          >
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {globalData?.saleitems?.map(
                                          (product, index) => (
                                            <tr key={index}>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  width: "5%",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {index + 1}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "left",
                                                  fontSize: "12px",
                                                  width: "65%",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                <div>{product.title}</div>
                                                {/* {product.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product.description}
                                      </div>
                                    )} */}
                                                {product.imode === "PRODUCT" &&
                                                  product.note && (
                                                    <div className="ms-0 fs-12 text-truncate-description text-muted">
                                                      {product?.note}
                                                    </div>
                                                  )}

                                                {/* <div>
                                      {product.hsnsac && (
                                        <span>
                                          <span className="fw-medium">
                                            HSN/SAC :{" "}
                                          </span>{" "}
                                          {product.hsnsac}
                                        </span>
                                      )}
                                      {product.discount > 0 && (
                                        <>
                                          <>
                                            {" "}
                                            <span className="fw-medium">
                                              MRP :{" "}
                                            </span>{" "}
                                            ₹
                                            {CurrencyConverter.toCurrency(
                                              product.mrp || 0,
                                              "INR"
                                            )}
                                          </>{" "}
                                          <>
                                            <span className="fw-medium">
                                              Discount :{" "}
                                            </span>{" "}
                                             ₹
                                            {CurrencyConverter.toCurrency(
                                              product.discount || 0,
                                              "INR"
                                            )}
                                          </>
                                        </>
                                      )}{" "}
                                      {product.taxpercentage > 0 && (
                                        <>
                                          <span className="fw-medium">
                                            Tax :{" "}
                                          </span>{" "}
                                          ₹
                                          {CurrencyConverter.toCurrency(
                                            product.baseprice || 0,
                                            "INR"
                                          )}
                                          @{product.taxpercentage}% = ₹
                                          {CurrencyConverter.toCurrency(
                                            product.taxamount || 0,
                                            "INR"
                                          )}
                                        </>
                                      )}
                                    </div> */}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {product.hsnsac}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product.mrp}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product.discount}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  width: "10%",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product.soldprice}
                                              </td>
                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  width: "10%",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                {product.quantity}
                                                <span className="font-11 ms-1">
                                                  {(() => {
                                                    const unitCode =
                                                      product?.isSecondaryUnit
                                                        ? product?.unitConfig
                                                            ?.secondaryUnitCode
                                                        : product?.unitConfig
                                                            ?.primaryUnitCode;

                                                    // return unitCode !== "unit" ? unitCode : "";
                                                    return unitCode || "";
                                                  })()}
                                                </span>
                                              </td>

                                              <td
                                                className="force-style"
                                                style={{
                                                  textAlign: "center",
                                                  fontSize: "12px",
                                                  width: "10%",
                                                  "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                }}
                                              >
                                                ₹{product.lineGrandTotal}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                        <tr>
                                          <td colSpan="4">
                                            Total Items :{" "}
                                            {globalData?.saleitems?.length}
                                          </td>
                                          <td
                                            colSpan="3"
                                            style={{
                                              textAlign: "end",
                                              fontSize: "14px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            Total Amount :
                                          </td>
                                          <td
                                            style={{
                                              textAlign: "end",
                                              fontSize: "14px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            ₹{globalData?.grandtotal}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td
                                            colSpan="7"
                                            style={{ verticalAlign: "top" }}
                                          >
                                            {globalData?.grandtotal > 0 && (
                                              <table
                                                className="items"
                                                width="50%"
                                                cellPadding="4"
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      colSpan={2}
                                                      className="force-style"
                                                      style={{
                                                        width: "100%",
                                                        textAlign: "center",
                                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        "--th-bg":
                                                          selectedTheme
                                                            ?.templateTheme
                                                            ?.primaryColor,
                                                        "--th-color":
                                                          selectedTheme
                                                            ?.templateTheme
                                                            ?.foregroundColor,
                                                      }}
                                                    >
                                                      Payment Mode
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {(globalData?.cashamount ||
                                                    0) -
                                                    (globalData?.cashchange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        Cash
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          fontWeight: "600",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹{globalData?.cashPaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.cardamount ||
                                                    0) -
                                                    (globalData?.cardchange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        Card
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          fontWeight: "600",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹{globalData?.cardPaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.onlineamount ||
                                                    0) -
                                                    (globalData?.onlinechange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        OnlineAmount
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          fontWeight: "600",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹
                                                        {globalData?.onlinePaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.upiamount ||
                                                    0) -
                                                    (globalData?.upichange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        UPI
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          fontWeight: "600",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹{globalData?.upiPaid}
                                                      </td>
                                                    </tr>
                                                  )}
                                                  {(globalData?.paylateramount ||
                                                    0) -
                                                    (globalData?.paylaterchange ||
                                                      0) >
                                                    0 && (
                                                    <tr>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        BalanceDue
                                                      </td>
                                                      <td
                                                        className="force-style"
                                                        style={{
                                                          textAlign: "start",
                                                          fontSize: "12px",
                                                          width: "10%",
                                                          fontWeight: "600",
                                                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                                        }}
                                                      >
                                                        ₹
                                                        {globalData?.balanceDue}
                                                      </td>
                                                    </tr>
                                                  )}
                                                </tbody>
                                              </table>
                                            )}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    </div>

                                    <div className="row mt-2">
                                                                      {terms?.length > 0 && (
                                                                        <div className="col-12 col-md-8 mb-3 mb-md-0">
                                                                          <div
                                                                            className="fs-13 fw-600"
                                                                            style={{
                                                                              textAlign: "left",
                                                                              color: "black",
                                                                            }}
                                                                          >
                                                                            Terms & Conditions
                                                                          </div>
                                                                          <div className="terms-box">{terms}</div>
                                                                        </div>
                                                                      )}
                                  
                                                                      <div className="col-12 col-md-4 d-flex justify-content-end ">
                                                                        <div>
                                                                          {globalData?.storeSettings?.invoiceSettings?.signature && (
                                                                            <div className="text-end p-0 fw-bold">
                                                                              <img
                                                                                src={
                                                                                  uRLs.IMAGE_URL +
                                                                                  globalData?.storeSettings?.invoiceSettings?.signature
                                                                                }
                                                                                width={150}
                                                                                alt="Signature"
                                                                              />
                                                                            </div>
                                                                          )}
                                  
                                                                          <div className="text-end p-0">
                                                                            Issued By, Signature
                                                                          </div>
                                  
                                                                          <div className="text-end p-0 fw-bold">
                                                                            {globalData?.salefrom?.title}
                                                                          </div>
                                                                        </div>
                                                                      </div>
                                                                    </div>
                                  </div>
                              </InvoiceWrapper>
                          
                        </>
                      ) : (
                        <div className="default-content">
                          <p>No content available for this sale mode.</p>
                        </div>
                      )}
                     </InvoiceLayout>
            
    </>
  );
};

export default StructuredInvoice;
